// Shared fetch / insert / update / soft-delete helpers for the unified
// Other Tasks system. All 8 dept-specific *_other_tasks tables share the
// same schema; this module reads the table name from OTHER_TASKS_TABLE_CONFIG.

import { supabase } from "@/integrations/supabase/client";
import { OTHER_TASKS_TABLE_CONFIG } from "./other-tasks-config";

export interface OtherTaskRow {
  id: string;
  user_id: string;
  task_date: string;
  start_time: string;
  end_time: string;
  task_type: string;
  notes: string;
  created_at: string;
  updated_at?: string;
  deleted_at?: string | null;
}

export interface OtherTaskInput {
  task_type: string;
  start_time: string;
  end_time: string;
  notes: string;
  /** Optional: defaults to today (IST) at the DB level via column default. */
  task_date?: string;
}

function requireConfig(department: string) {
  const cfg = OTHER_TASKS_TABLE_CONFIG[department];
  if (!cfg) throw new Error(`Unknown department for other-tasks: ${department}`);
  return cfg;
}

export async function fetchMyOtherTasksForDept(
  userId: string,
  department: string,
): Promise<OtherTaskRow[]> {
  const cfg = OTHER_TASKS_TABLE_CONFIG[department];
  if (!cfg) return [];
  const { data, error } = await supabase
    .from(cfg.tableName as never)
    .select("*")
    .eq("user_id", userId)
    .is("deleted_at", null)
    .order("task_date", { ascending: false })
    .order("start_time", { ascending: false });
  if (error) {
    console.warn(`fetchMyOtherTasksForDept(${department}):`, error);
    return [];
  }
  return (data ?? []) as unknown as OtherTaskRow[];
}

export async function insertOtherTaskForDept(
  userId: string,
  department: string,
  input: OtherTaskInput,
): Promise<void> {
  const cfg = requireConfig(department);
  const payload: Record<string, unknown> = {
    user_id: userId,
    task_type: input.task_type,
    start_time: input.start_time,
    end_time: input.end_time,
    notes: input.notes,
  };
  if (input.task_date) payload.task_date = input.task_date;
  const { error } = await supabase
    .from(cfg.tableName as never)
    .insert(payload as never);
  if (error) throw error;
}

export async function updateOtherTaskForDept(
  id: string,
  department: string,
  input: OtherTaskInput,
): Promise<void> {
  const cfg = requireConfig(department);
  const payload: Record<string, unknown> = {
    task_type: input.task_type,
    start_time: input.start_time,
    end_time: input.end_time,
    notes: input.notes,
  };
  if (input.task_date) payload.task_date = input.task_date;
  const { error } = await supabase
    .from(cfg.tableName as never)
    .update(payload as never)
    .eq("id", id);
  if (error) throw error;
}

export async function softDeleteOtherTaskForDept(
  id: string,
  department: string,
): Promise<void> {
  const cfg = requireConfig(department);
  const { error } = await supabase
    .from(cfg.tableName as never)
    .update({ deleted_at: new Date().toISOString() } as never)
    .eq("id", id);
  if (error) throw error;
}
